import * as Dialog from '@radix-ui/react-dialog'

import { XIcon, IconButton, VisuallyHidden } from '@buffer-mono/popcorn'
import { Upload } from '@buffer-mono/uploader'

import { DocumentPreview } from '~publish/legacy/composer/composer/components/document-attachment/DocumentPreview'
import { getHumanReadableSize } from '~publish/legacy/composer/composer/utils/StringUtils'

import ImageDescription from './ImageDescription'
import styles from './styles.module.css'

import type { JSX } from 'react'

export const MediaPreviewer = ({
  upload,
  containerRef,
  onClose,
}: {
  upload: Upload | null
  containerRef?: HTMLElement | null
  onClose: () => void
}): JSX.Element => {
  const isOpen = !!upload

  const setIsOpen = (open: boolean): void => {
    if (!open) {
      onClose()
    }
  }

  if (!upload) return <></>

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal container={containerRef}>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.modalWrapper}>
          <VisuallyHidden>
            <Dialog.Title>Media Preview</Dialog.Title>
          </VisuallyHidden>
          <div className={styles.innerWrapper}>
            {(Upload.isImage(upload) || Upload.isGif(upload)) && (
              <>
                <div className={styles.mediaContainer}>
                  <img
                    className={styles.media}
                    alt={upload.alt}
                    src={upload.url}
                  />
                </div>
                <ImageDescription upload={upload} onClose={onClose} />
              </>
            )}
            {Upload.isVideo(upload) && (
              <div className={styles.mediaContainer}>
                <video
                  className={styles.media}
                  poster={upload.thumbnailUrl}
                  preload="metadata"
                  controls
                  src={upload.url}
                  aria-label="Video media preview"
                >
                  <track kind="captions" />
                </video>
                <div className={styles.fileSize}>
                  {getHumanReadableSize(upload.size)}
                </div>
              </div>
            )}
            {Upload.isDocumentUpload(upload) && (
              <div className={styles.mediaContainer}>
                <DocumentPreview
                  title={upload.alt || 'Document'}
                  url={upload.url}
                  height={520}
                />
                <div className={styles.fileSize}>
                  {getHumanReadableSize(upload.size)}
                </div>
              </div>
            )}
            <Dialog.Close asChild>
              <IconButton
                label="Close"
                variant="tertiary"
                size="small"
                className={styles.closeIcon}
              >
                <XIcon />
              </IconButton>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
